import React, { useState } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { injectIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { MEDIA_TYPE_VIDEO, MEDIA_TYPE_PHOTO } from '../../util/types';

import { useConfiguration } from '../../context/configurationContext';
import { Field } from 'react-final-form';
import { IconSpinner } from '../../components';

import css from './FieldMediaUploader.module.css';
import classNames from 'classnames';

export const VALID_VIDEO_FORMATS = ['video/mp4', 'video/quicktime', 'video/mov', 'mp4', 'mov', 'quicktime'];
export const MAX_VIDEO_SIZE = 20000000;
export const MAX_VIDEO_LENGTH = 5;

export const VALID_PHOTO_FORMATS = ['image/jpeg', 'image/jpg', 'image/tiff', 'image/png', 'jpeg', 'jpg', 'tiff', 'png'];
export const MAX_PHOTO_SIZE = 5000000;
export const MAX_PHOTO_LENGTH = 15;

export const VALID_FORMATS = [...VALID_PHOTO_FORMATS, ...VALID_VIDEO_FORMATS];

export const isPhotoType = file => {
  return !!VALID_PHOTO_FORMATS.filter(f => f.indexOf(file?.format || file?.type) !== -1)?.length
}
export const isVideoType = file => {
  return !!VALID_VIDEO_FORMATS.filter(f => f.indexOf(file?.format || file?.type) !== -1)?.length
}

const FieldMediaUploaderComponent = props => {
  const {
    intl,
    id,
    onUploadMedia,
    onDeleteMedia,
    media,
    mediaName,
    uploadMediaInProgress,
    deleteMediaInProgress,
    uploadMediaError,
    deleteMediaError,
    entity,
    userRole,
    handleSetCurrentMedia,
    setCurrentMediaData,
    isMobSaf,
    handleShowCrop
  } = props;

  const [mediaKey, setMediaKey] = useState(null)
  const [mediaValidationError, setMediaValidationError] = useState(null);

  return (
    <Field form={null} name={mediaName} type="file" multiple>
      {fieldprops => {
        const { accept, input } = fieldprops;
        const { name, type } = input;

        const onChange = e => {
          if (uploadMediaInProgress || !e.target.files[0]) return;

          const files = e.target.files;
          const uploadedFiles = [...e.target.files]

          const invalidFormat = !!uploadedFiles.filter(f => !VALID_FORMATS.includes(f.type))?.length;
          const invalidSize = !!uploadedFiles.filter(f => {
            let maxSize
            if (isPhotoType(f)) maxSize = MAX_PHOTO_SIZE
            if (isVideoType(f)) maxSize = MAX_VIDEO_SIZE
            return f.size > maxSize
          })?.length;

          const allFiles = [...uploadedFiles, ...media];
          const videoCount = allFiles.filter(item => VALID_VIDEO_FORMATS.includes(item.format || item.type)).length;
          const photoCount = allFiles.filter(item => VALID_PHOTO_FORMATS.includes(item.format || item.type)).length;

          const invalidFilesLength =
            videoCount > MAX_VIDEO_LENGTH ||
            photoCount > MAX_PHOTO_LENGTH ||
            videoCount + photoCount > MAX_VIDEO_LENGTH + MAX_PHOTO_LENGTH;

          invalidFilesLength && setMediaValidationError(<FormattedMessage id="FieldMediaUploader.invalidMediaLength" />);
          invalidFormat && setMediaValidationError(<FormattedMessage id="FieldMediaUploader.invalidMediaFormat" />);
          invalidSize && setMediaValidationError(
            <FormattedMessage
              id="FieldMediaUploader.invalidMediaSize"
              values={{
                maxSizePhoto: (MAX_PHOTO_SIZE / 1000000),
                maxSizeVideo: (MAX_VIDEO_SIZE / 1000000)
              }}
            />
          );

          !invalidFormat && !invalidSize && !invalidFilesLength && setMediaValidationError(null);

          if (!invalidFormat && !invalidSize && !invalidFilesLength) {
            onUploadMedia(files, id, userRole, entity, mediaName);
            handleSetCurrentMedia(mediaName)
            setCurrentMediaData(files, id, userRole, entity, mediaName, invalidFormat, invalidSize, invalidFilesLength)
          }
        };

        const onHandleDeleteMedia = async (key) => {
          setMediaKey(key);
          setMediaValidationError(null);
          await onDeleteMedia(key, id, entity, mediaName);
        }

        const inputProps = {
          id: name,
          name,
          ...(isMobSaf ? {onChange} : {
            onClick: e => handleShowCrop(e),
          }),
          type,
          multiple: true,
          accept: ".jpeg, .jpg, .tiff, .png, .mp4, .mov"
        };

        return (
          <>
            <div className={css.mediaFieldArray}>
              {!!media?.length && media.map(mediaFile => {

                const isPhoto = isPhotoType(mediaFile);

                return (
                  <div key={mediaFile.key}
                    className={classNames(
                      css.thumbnailHolder,
                      { [css.deleteInProgress]: deleteMediaInProgress && mediaKey === mediaFile.key }
                    )}
                  >
                    {isPhoto ? (
                      <img src={mediaFile.location} alt={mediaFile.key} className={css.thumbnail} />

                    ) : (
                      <video controls className={css.thumbnail}>
                        <source src={mediaFile.location} type={`video/mp4`} />
                        Your browser does not support the video tag.
                      </video>
                    )}

                    <button
                      className={css.removeMedia}
                      onClick={() => onHandleDeleteMedia(mediaFile.key)}
                      type="button"
                    >
                      <svg
                        width="10px"
                        height="10px"
                        viewBox="0 0 10 10"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g strokeWidth="1" fillRule="evenodd">
                          <g transform="translate(-821.000000, -311.000000)">
                            <g transform="translate(809.000000, 299.000000)">
                              <path
                                d="M21.5833333,16.5833333 L17.4166667,16.5833333 L17.4166667,12.4170833 C17.4166667,12.1866667 17.2391667,12 17.00875,12 C16.77875,12 16.5920833,12.18625 16.5920833,12.41625 L16.5883333,16.5833333 L12.4166667,16.5833333 C12.18625,16.5833333 12,16.7695833 12,17 C12,17.23 12.18625,17.4166667 12.4166667,17.4166667 L16.5875,17.4166667 L16.5833333,21.5829167 C16.5829167,21.8129167 16.7691667,21.9995833 16.9991667,22 L16.9995833,22 C17.2295833,22 17.41625,21.81375 17.4166667,21.58375 L17.4166667,17.4166667 L21.5833333,17.4166667 C21.8133333,17.4166667 22,17.23 22,17 C22,16.7695833 21.8133333,16.5833333 21.5833333,16.5833333"
                                transform="translate(17.000000, 17.000000) rotate(-45.000000) translate(-17.000000, -17.000000) "
                              />
                            </g>
                          </g>
                        </g>
                      </svg>
                    </button>
                  </div>
                )
              })}

              {media?.length < MAX_VIDEO_LENGTH + MAX_PHOTO_LENGTH && (
                <div className={css.addMediaWrapper}>
                  <input {...inputProps} className={css.addMediaInput} disabled={uploadMediaInProgress} />
                  <label htmlFor={mediaName} className={css.addMedia}>
                    <span className={css.chooseMediaText}>
                      {uploadMediaInProgress ? (
                        <IconSpinner rootClassName={css.uploadButtonSpinner} />
                      ) : (
                        <>
                          <span className={css.chooseMedia}>
                            <FormattedMessage id="FieldMediaUploader.uploadMedia" />
                          </span>
                          <span className={css.mediaTypes}>
                            <FormattedMessage
                              id="FieldMediaUploader.uploadMediaInfo"
                              values={{
                                maxPhotoSize: (MAX_PHOTO_SIZE / 1000000),
                                maxVideoSize: (MAX_VIDEO_SIZE / 1000000),
                                lineBreak: <br />
                              }}
                            />
                          </span>
                        </>
                      )}
                    </span>
                  </label>
                </div>
              )}
            </div>

            {mediaValidationError && (
              <p className={css.error}>
                {mediaValidationError}
              </p>
            )}
            {uploadMediaError && (
              <p className={css.error}>
                <FormattedMessage
                  id="FieldMediaUploader.uploadMediaError"
                />
              </p>
            )}
            {deleteMediaError && (
              <p className={css.error}>
                <FormattedMessage
                  id="FieldMediaUploader.deleteMediaError"
                />
              </p>
            )}
          </>
        );
      }}
    </Field>
  );
};

FieldMediaUploaderComponent.propTypes = {
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {

  const {
    uploadFileError,
    uploadSuccess,
    files,
    deleteFileInProgress,
    deleteFileError,
  } = state.files;

  return {
    uploadMediaError: uploadFileError,
    uploadSuccess,
    files,
    deleteMediaInProgress: deleteFileInProgress,
    deleteMediaError: deleteFileError,
  };
};

const FieldMediaUploader = compose(
  withRouter,
  connect(
    mapStateToProps,
  ),
  injectIntl
)(FieldMediaUploaderComponent);

export default FieldMediaUploader;
